// Sign-in walls: the handful of providers that deliberately refuse to authenticate
// through any proxy, and what to tell someone who walks into one.
//
// This is not a limitation anybody can patch. Google, Microsoft and Apple all detect
// proxied and embedded browsers on their sign-in pages and stop the flow on purpose,
// because a proxy sitting between a person and their password is exactly the shape of
// an account-theft attack. Three things stack up here at once:
//
//   1. The provider fingerprints the client and refuses ("this browser or app may not
//      be secure"). That check exists to protect the account and it is working.
//   2. The engine rewrites the page's JavaScript so it can proxy it, and sign-in flows
//      lean on cross-origin messaging, WebAuthn and integrity checks that do not
//      survive being rewritten.
//   3. Everything arrives from a datacenter address rather than a home connection,
//      which is a strong signal for exactly the kind of takeover attempt this is
//      designed to stop.
//
// So the honest thing is to say so up front instead of letting someone type their
// password into a page that was never going to work. The notice is never a hard block:
// "Try anyway" loads it regardless, because being wrong about a URL must not be the
// thing that stops somebody reaching a site.

const WALLS = [
	{
		id: "google",
		label: "Google",
		test: (h, p) =>
			h === "accounts.google.com" ||
			h === "accounts.youtube.com" ||
			(/^(www\.)?google\.[a-z.]+$/.test(h) && /^\/(signin|accounts|ServiceLogin)/i.test(p)),
	},
	{
		id: "microsoft",
		label: "Microsoft",
		test: (h) => h === "login.microsoftonline.com" || h === "login.live.com" || h === "login.microsoft.com",
	},
	{
		id: "apple",
		label: "Apple",
		test: (h) => h === "appleid.apple.com" || h === "idmsa.apple.com",
	},
];

/**
 * Does this URL land on a provider sign-in page that will refuse a proxy?
 * Returns { id, label } or null. Never throws on a malformed URL.
 */
export function signInWall(url) {
	let u;
	try {
		u = new URL(url);
	} catch (e) {
		return null;
	}
	if (!/^https?:$/.test(u.protocol)) return null;
	const host = u.hostname.toLowerCase().replace(/\.$/, "");
	const pathname = u.pathname || "/";
	for (const w of WALLS) {
		if (w.test(host, pathname)) return { id: w.id, label: w.label };
	}
	return null;
}

/**
 * The copy for the notice. Kept next to the detection so the two cannot drift, and
 * written to be read by somebody who is annoyed and in a hurry.
 */
export function signInWallText(wall) {
    const label = (wall && wall.label) || "This provider";
	return {
		title: label + " blocks sign-in through a proxy",
		message:
			label +
			" deliberately refuses to sign you in from a proxied browser. It is not a bug here and it is not " +
			"something switching engines or transports will get around: the check exists to stop someone " +
			"putting themselves between you and your password, and from " +
			label +
			"'s side that is exactly what this looks like. Browsing " +
			label +
			" while already signed in on this device usually still works. Signing IN does not.",
	};
}
